import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { usePatient } from "@/context/PatientContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Generic CRUD page.
 * fields = [{ key, label, type, placeholder, required, textarea, options }]
 */
export default function ResourceList({
  title, subtitle, resource, fields, itemTitle, itemSubtitle, icon: Icon,
  itemBadge, extraActions, emptyText, testidPrefix,
}) {
  const { activePatient, canEdit } = usePatient();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const initial = Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""]));
  const [form, setForm] = useState(initial);

  const load = useCallback(async () => {
    if (!activePatient) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/${resource}`, { params: { patient_id: activePatient.id } });
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [activePatient, resource]);

  useEffect(() => { load(); }, [load]);

  const startNew = () => {
    setEditingId(null);
    setForm(initial);
    setOpen(true);
  };
  const startEdit = (item) => {
    setEditingId(item.id);
    const merged = { ...initial };
    for (const k of Object.keys(initial)) if (item[k] != null) merged[k] = item[k];
    setForm(merged);
    setOpen(true);
  };
  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, patient_id: activePatient.id };
      if (editingId) {
        const { data } = await api.patch(`/${resource}/${editingId}`, payload);
        setItems((xs) => xs.map((x) => (x.id === editingId ? data : x)));
        toast.success("Updated");
      } else {
        const { data } = await api.post(`/${resource}`, payload);
        setItems((xs) => [data, ...xs]);
        toast.success("Added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };
  const remove = async (item) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/${resource}/${item.id}`);
    setItems((xs) => xs.filter((x) => x.id !== item.id));
    toast.success("Deleted");
  };

  if (!activePatient) return <div className="text-[#576A62]">Add a patient first.</div>;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">{subtitle}</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-bold">{title}</h1>
        </div>
        <button
          data-testid={`${testidPrefix}-add-btn`}
          onClick={startNew}
          disabled={!canEdit}
          title={canEdit ? "" : "You have viewer access"}
          className="inline-flex items-center gap-2 rounded-full bg-[#2C5545] hover:bg-[#224235] disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-3 font-semibold transition-colors"
        >
          <Plus weight="bold" size={16} /> Add
        </button>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[0,1,2,3,4,5].map((i) => <div key={i} className="h-40 rounded-2xl bg-[#EBE8E0]/50 animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#EBE8E0] p-12 text-center bg-white">
          {Icon && <Icon size={56} weight="duotone" className="text-[#2C5545] mx-auto" />}
          <div className="mt-4 font-heading text-2xl font-bold">{emptyText || "Nothing yet"}</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((item) => (
            <div key={item.id} data-testid={`${testidPrefix}-card-${item.id}`} className="bg-white border border-[#EBE8E0] rounded-2xl p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-heading text-lg font-bold truncate">{itemTitle(item)}</div>
                  <div className="text-sm text-[#576A62] mt-0.5">{itemSubtitle?.(item)}</div>
                  {itemBadge?.(item)}
                </div>
                {Icon && <Icon size={28} weight="duotone" className="text-[#2C5545] shrink-0" />}
              </div>
              {extraActions?.(item, load)}
              {canEdit && (
                <div className="mt-4 flex items-center gap-2">
                  <button
                    onClick={() => startEdit(item)}
                    data-testid={`${testidPrefix}-edit-${item.id}`}
                    className="flex-1 rounded-full border border-[#EBE8E0] hover:bg-[#EBE8E0]/60 px-3 py-1.5 text-sm inline-flex items-center justify-center gap-1"
                  ><PencilSimple size={14} /> Edit</button>
                  <button
                    onClick={() => remove(item)}
                    data-testid={`${testidPrefix}-delete-${item.id}`}
                    className="rounded-full border border-[#EBE8E0] hover:bg-[#C86A51]/10 text-[#C86A51] px-3 py-1.5 text-sm inline-flex items-center gap-1"
                  ><Trash size={14} /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editingId ? `Edit ${title.slice(0,-1) || title}` : `Add ${title.slice(0,-1) || title}`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            {fields.map((f) => (
              <FormField key={f.key} f={f} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testidPrefix={testidPrefix} />
            ))}
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border border-[#EBE8E0] text-[#576A62] hover:bg-[#EBE8E0]/60">Cancel</button>
              <button
                data-testid={`${testidPrefix}-save`}
                type="submit"
                className="rounded-full px-5 py-2.5 bg-[#2C5545] hover:bg-[#224235] text-white font-semibold"
              >Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FormField({ f, value, onChange, testidPrefix }) {
  const testid = `${testidPrefix}-form-${f.key}`;
  if (f.type === "checkbox") {
    return (
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          data-testid={testid}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          className="w-5 h-5 rounded accent-[#2C5545]"
        />
        <span className="text-sm">{f.label}</span>
      </label>
    );
  }
  if (f.textarea) {
    return (
      <label className="block">
        <div className="text-sm font-medium mb-1.5">{f.label}{f.required && <span className="text-[#C86A51]"> *</span>}</div>
        <textarea
          data-testid={testid}
          value={value || ""}
          required={f.required}
          onChange={(e) => onChange(e.target.value)}
          placeholder={f.placeholder}
          rows={f.rows || 3}
          className="w-full rounded-xl border border-[#EBE8E0] bg-white px-4 py-3 focus:ring-2 focus:ring-[#2C5545] outline-none"
        />
      </label>
    );
  }
  if (f.options) {
    return (
      <label className="block">
        <div className="text-sm font-medium mb-1.5">{f.label}</div>
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-3"
        >
          <option value="">Select…</option>
          {f.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </label>
    );
  }
  return (
    <label className="block">
      <div className="text-sm font-medium mb-1.5">{f.label}{f.required && <span className="text-[#C86A51]"> *</span>}</div>
      <input
        data-testid={testid}
        type={f.type || "text"}
        required={f.required}
        value={value ?? ""}
        onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        placeholder={f.placeholder}
        className="w-full h-11 rounded-xl border border-[#EBE8E0] bg-white px-4 focus:ring-2 focus:ring-[#2C5545] outline-none"
      />
    </label>
  );
}
